import { LegacyRef, ReactElement, forwardRef, useRef, useState } from 'react';

import { ComboBoxInputProps } from './combo-box.types';
import {
  MagnifyingGlassIcon,
  Tag,
  TagProps,
  XmarkIcon,
  mergeStyles,
  stopKeyboardEventHandler,
  stopMousePropagation,
} from '../..';
import { MenuItem } from '../filter-combo-box/filter-combo-box.types';

interface ComboBoxTagProps {
  item: MenuItem;
  onRightClick?: () => void;
  tagProps: Partial<TagProps>;
}

const ComboBoxTag = ({ item, onRightClick, tagProps }: ComboBoxTagProps) => {
  return <Tag text={item.label} baseType="table" rightIcon={XmarkIcon} onRightClick={onRightClick} {...tagProps} />;
};

// eslint-disable-next-line max-lines-per-function
const ComboBoxInputWithoutRef = <T extends MenuItem>(props: ComboBoxInputProps<T>, ref?: LegacyRef<HTMLDivElement>) => {
  const {
    selectedItems,
    autoFocus,
    placeholder,
    className,
    style,
    size = 20,
    onSearch,
    onDeselect,
    onFocus,
    onBlur,
    value,
    defaultValue,
    tagProps,
    containerSize,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [activeElementWhenWrapperWasSelected, setActiveElementWhenWrapperWasSelected] = useState<Element | undefined>(
    undefined
  );
  const [wrapperWasLastSelected, setWrapperWasLastSelected] = useState<boolean>(false);

  return (
    <div
      data-testid="combobox-input-wrapper"
      ref={ref}
      style={style}
      className={mergeStyles([`combobox-input-wrapper group/comboboxitem size-${containerSize}`, className])}
      onKeyDown={stopKeyboardEventHandler}
      onMouseDown={() => {
        setWrapperWasLastSelected(true);
        setActiveElementWhenWrapperWasSelected(document.activeElement ?? undefined);
      }}
      onClick={(e) => {
        if (activeElementWhenWrapperWasSelected !== inputRef?.current) inputRef?.current?.focus?.();
        stopMousePropagation(e);
      }}
    >
      <div className="browser-style-none">
        {selectedItems.map((item) => {
          const customProps = (typeof tagProps === 'function' ? tagProps(item) : tagProps) ?? {};
          return <ComboBoxTag key={item.value} item={item} onRightClick={onDeselect?.(item)} tagProps={customProps} />;
        })}
        <input
          ref={inputRef}
          role="searchbox"
          autoFocus={autoFocus}
          type="search"
          size={size}
          className="ring-0"
          placeholder={placeholder}
          onMouseDown={stopMousePropagation}
          onFocus={(e) => {
            setWrapperWasLastSelected(false);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            if (wrapperWasLastSelected) e.target.focus();
            setWrapperWasLastSelected(false);
            onBlur?.(e);
          }}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => onSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && selectedItems.length > 0 && value === '') {
              onDeselect?.(selectedItems[selectedItems.length - 1])();
            }
          }}
        />
      </div>
      <div className="combobox-icon-wrapper">
        <MagnifyingGlassIcon className="size-5 fill-gray-600" />
      </div>
    </div>
  );
};

export const ComboBoxInput = forwardRef(ComboBoxInputWithoutRef) as <T extends MenuItem>(
  p: ComboBoxInputProps<T> & { ref?: LegacyRef<HTMLDivElement> }
) => ReactElement;
